/**
 * Canonical builder for `GET /api/address-book` — read-only listing of the
 * seller's saved addresses. Boundary-parsed via `AddressBookListResponseSchema`
 * so consumers receive a typed `AddressBookListResponse` and never branch on
 * raw JSON. Centralised per the Frontend HARD GATE: components under fenced
 * folders (e.g. `components/shipping-settings/**`) must not call `/api/*`
 * inline.
 *
 * First consumer:
 *   - `components/shipping-settings/auto-inferred-address-banner.tsx`
 *     (WS8.6 surfaces only the `label`/`is_main`/`deleted_at` triad — see
 *     `packages/contract/src/address-book.ts` for the minimal pin scope).
 *
 * @see packages/contract/src/address-book.ts — schema + interface (SoT)
 * @see app/api/address-book/route.ts — server emitter
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §3.2 — fence rationale
 */

import {
  parseApiResponse,
  AddressBookListResponseSchema,
  type AddressBookListResponse,
} from "@kraahl/contract";

export interface ListAddressesArgs {
  /** Optional abort signal — threaded into `fetch`. */
  signal?: AbortSignal;
}

/**
 * Fetch the authenticated seller's address book and return it boundary-
 * parsed as `AddressBookListResponse`. Throws on non-2xx or schema mismatch
 * (`ApiResponseParseError` from `@kraahl/contract`). Does NOT swallow —
 * callers decide whether to suppress (informational UI) or surface.
 */
export async function listAddresses(
  args: ListAddressesArgs = {},
): Promise<AddressBookListResponse> {
  const res = await fetch("/api/address-book", {
    ...(args.signal ? { signal: args.signal } : {}),
  });

  if (!res.ok) {
    throw new Error(`Address book API response not ok: ${res.status}`);
  }

  return parseApiResponse(res, AddressBookListResponseSchema);
}
